import { t } from '@/i18n';

/**
 * The Mac app as a file, straight from this site: signed with Passwork's Developer ID and notarized by Apple, so
 * it opens without a warning.
 */
export const MAC_DMG_URL = '/download/Bridge.dmg';

export const MAC_DIRECT = {
  href: MAC_DMG_URL,
  file: 'Bridge.dmg',
  /** Read when the window renders, so it follows the language; `os` is the oldest macOS the published build runs on. */
  requires(os = '13'): string {
    return t('appLinks.macRequires', { os });
  },
};

/** The iPhone and iPad apps are not out yet: the download window lists them as coming, with no link. */
export const IOS_SOON = {
  get name(): string {
    return t('appLinks.iosName');
  },
  get requires(): string {
    return t('appLinks.iosRequires');
  },
};

export type Platform = 'mac' | 'iphone' | 'ipad';
type Device = Platform | 'other';

export interface AppPlatform {
  readonly id: Platform;
  /** What the main button says when this is the visitor's device. */
  readonly action: string;
}

/** The apps the main button downloads: only the Mac app, from this site. */
const DOWNLOADABLE: readonly AppPlatform[] = [
  {
    id: 'mac',
    get action(): string {
      return t('appLinks.downloadMac');
    },
  },
];

/** The visitor's device, from what the browser says about itself. iPadOS calls itself a Mac, with a touch screen. */
export function deviceOf(userAgent: string, maxTouchPoints: number): Device {
  if (/iPhone|iPod/.test(userAgent)) return 'iphone';
  if (userAgent.includes('iPad') || (userAgent.includes('Macintosh') && maxTouchPoints > 1)) return 'ipad';
  if (userAgent.includes('Macintosh')) return 'mac';
  return 'other';
}

/** The app to download on this device, or null where there is none to download (Windows, Android, Linux, iPhone, iPad). */
export function platformFor(device: Device): AppPlatform | null {
  return DOWNLOADABLE.find((platform) => platform.id === device) ?? null;
}

/** The app for the browser this page runs in. */
export function currentPlatform(): AppPlatform | null {
  if (typeof navigator === 'undefined') return null;
  return platformFor(deviceOf(navigator.userAgent, navigator.maxTouchPoints));
}
